<template>
  <!-- Chat -->
  <div v-if="conversation" class="chat chat-messages" id="middle">
    <div class="chat-container">
      <div class="chat-header">
        <div class="user-details">
          <div class="d-lg-none">
            <ul class="list-inline mt-2 me-2">
              <li class="list-inline-item">
                <a
                  class="text-muted px-0 left_side"
                  href="#"
                  data-chat="open"
                  @click="closeConversation"
                >
                  <i class="fas fa-arrow-left"></i>
                </a>
              </li>
            </ul>
          </div>

          <router-link :to="`/user/${conversation.user.username}/profile`">
            <UserAvatar :user="conversation.user" class="me-2" online/>
          </router-link>

          <div>
            <router-link :to="`/user/${conversation.user.username}/profile`">
              <h5>{{ conversation.user.profile.display_name }}</h5>
            </router-link>

            <small v-if="isTyping">
              <Typing text="Typing"/>
            </small>

            <small v-else-if="conversation.messages.length" class="last-seen">{{ timeFormat(lastMessageTimestamp(lastMessage(conversation)), 'chat-window') }}</small>
          </div>
        </div>

        <div class="chat-options">
          <ul class="list-inline">
            <li v-if="canCall" class="list-inline-item">
              <a-tooltip :mouseEnterDelay="0" :mouseLeaveDelay="0"
                         :title="(!this.canCallWithAudio && !this.canCallWithVideo) ? 'Upgrade' : (chatScreen === 'call' ? $t('chat') : $t('call_video'))" placement="bottom"
              >
                <a
                  href="#"
                  class="btn btn-outline-light not-chat-user action-button info"
                  :style="!this.canCallWithAudio && !this.canCallWithVideo ? 'background: #bdbdbd !important; cursor: not-allowed; color: #933030 !important;' : ''"
                  data-bs-toggle="tooltip"
                  data-bs-placement="bottom"
                  :title="(!this.canCallWithAudio && !this.canCallWithVideo) ? 'Upgrade' : (chatScreen === 'call' ? $t('chat') : $t('call_video'))"
                  @click.prevent="toggleChatScreen"
                >
                  <i v-show="chatScreen === 'chat'" class="bx bx-phone"></i>
                  <i v-show="chatScreen === 'call'" class="feather-message-square" data-feather="message-square"></i>
                </a>
              </a-tooltip>
            </li>

            <li class="list-inline-item">
              <a-tooltip :mouseEnterDelay="0" :mouseLeaveDelay="0" :title="$t('chat_conversation_view_profile_hover')" placement="bottom">
                <router-link
                  :to="`/user/${conversation.user.username}/profile`"
                  class="btn btn-outline-light not-chat-user action-button info"
                  data-bs-toggle="tooltip"
                  data-bs-placement="bottom"
                  title="User profile"
                >
                  <i class="bx bx-info-circle"></i>
                </router-link>
              </a-tooltip>
            </li>

            <li class="list-inline-item" @click="closeWindow">
              <a
                href="#"
                class="not-chat-user action-button close"
              >
                <i class="bx bx-x"></i>
              </a>
            </li>
          </ul>
        </div>
      </div>

      <ChatCallWindow v-show="chatScreen === 'call'" :user="conversation.user" @close="CloseCall"/>

      <div v-show="chatScreen === 'chat'" class="scroll-area-two">
        <div id="chat-scroll" class="chat-body chat-page-group h-full" :class="{'no-message': !chatMessages.length}">
          <div v-if="loading" class="w-full h-full d-flex justify-content-center align-items-center">
            <Loading/>
          </div>

          <div v-else-if="chatMessages.length" class="messages">
            <div class="message-day-group">
              <template
                v-for="message in chatMessages"
                :key="message.id"
              >
                <chat-message
                  v-if="message.user.profile"
                  :conversation-id="conversation.id"
                  :message="message"
                />
              </template>
            </div>
          </div>

          <div v-else>
            <img
              src="@/assets/chat-image.png"
              alt="New conversation"
              class="img-fluid w-75 mb-5"
            />
            <h5>{{$t('chat_conversation_start_title')}}</h5>
            <p class="small">{{$t('chat_conversation_start_subtitle')}}</p>
          </div>
        </div>
      </div>

      <div v-show="chatScreen === 'chat'" class="chat-footer">
        <form class="chat-type shadow-sm">
          <div class="replay-forms">
            <input
              id="chat-input"
              v-model="newMessage"
              type="text"
              class="form-control chat_form"
              :placeholder="messageLimitExceeded ? $t('chat_conversation_trial_expired') : $t('chat_conversation_type_message')"
              @input="typing"
              :disabled="messageLimitExceeded"
            />

            <emoji-type v-if="!messageLimitExceeded" :list="emojiList" class="position-absolute start-0 bottom-0" @select="addEmoji"></emoji-type>
          </div>

          <div class="form-buttons">
            <button class="btn send-btn" type="submit" @click.prevent="onSendMessage" :disabled="messageLimitExceeded">
              {{ $t('chat_conversation_send') }} <i class="bx bx-paper-plane ms-1"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
  <!-- /Chat -->
</template>

<script>
import { Tooltip, Button } from "ant-design-vue";
import ChatSidebar from "@/assets/json/frontend/chat-sidebar.json";
import ChatMessage from "@/views/frontend/pages/chat/chat-message";
import {mapGetters} from "vuex";
import UserAvatar from "@/components/frontend/UserAvatar";
import ChatCallWindow from "@/components/frontend/ChatCallWindow";
const {timeFormat} = require("@/util");

export default {
  components: {
    ChatCallWindow,
    UserAvatar,
    ChatMessage,
    "a-tooltip": Tooltip,
    "a-button": Button,
  },

  computed: {
    ...mapGetters({
      canCallWithAudio: 'subscriptions/canCallWithAudio',
      canCallWithVideo: 'subscriptions/canCallWithVideo',
      lastMessage: 'chat/lastMessage',
      onGoingCall: 'chat/onGoingCall',
      peerState: 'chat/peerState',
      messageLimitExceeded: 'chat/messageLimitExceeded',
      conversations: 'chat/conversations',
      currentChatUser: 'chat/currentChatUser',
      contacts: 'chat/contacts',
      activeUsers: 'chat/activeUsers',
      conversationTypingMap: 'chat/conversationTyping',
      isOperator: 'isOperator',
    }),

    canCall() {
      return this.conversation && this.conversation.id !== 'new';
    },

    conversationTyping() {
      return this.conversation ? this.conversationTypingMap(this.conversation.id) : false;
    },

    conversation() {
      return this.conversations.find(e => e.user.username === this.$route.params.id);
    },

    chatMessages() {
      return (this.conversation.messages || []).filter(e => e.type !== 'call');
    },
  },

  data() {
    return {
      chatScreen: 'chat',
      emojiList: [
        '🙂',
        '😜',
        '💪',
        '😍',
        '😉',
        '🥰',
      ],
      loading: true,
      chat: null,
      chatSidebar: ChatSidebar,
      isTyping: false,
      isCurrentUserTyping: false,
      newMessage: '',
      settings: {
        suppressScrollX: true,
        useBothWheelAxes: false,
        wheelSpeed: 2,
      },
      showSidebar: false,
      showSidebarTwo: false,
      showSidebarThree: false,
    };
  },

  watch: {
    onGoingCall: {
      handler() {
        if (!this.onGoingCall) {
          this.CloseCall();
        }
      },
      immediate: true,
    },

    $route: {
      handler() {
        this.newMessage = '';

        if (this.conversation) {
          this.sendTyping(false);
        }
      },
      immediate: true,
    },

    conversationTyping: {
      handler() {
        this.isTyping = this.conversationTyping;

        if (this.isTyping) {
          this.scrollBottom();
        }
      },
      immediate: true,
    },

    '$route.params.id': {
      handler () {
        if (this.$route.params.id) {
          this.scrollBottom();
        }

        if (this.peerState === 'connected') {
          this.StartCall();
        }
      },
      immediate: true,
    }
  },

  created() {
    //
  },

  mounted() {
    this.$eventHub.$on('VideoChatCall', this.VideoChatCall);
    this.$eventHub.$on('toggleSidebar', this.toggleSidebar);
    this.$eventHub.$on('toggleSecondSidebar', this.toggleSidebarTwo);
    this.$eventHub.$on('toggleThirdSidebar', this.toggleSidebarThree);
    this.$eventHub.$on('ChatMessage', this.receiveChatMessage);
    this.$eventHub.$on('StartCall', this.StartCall);
    window.addEventListener('keydown', this.onKeyPress);

    if (this.conversation && this.conversation.id !== 'new' && this.conversation.user.id && this.conversation.messages.length) {
      // Send events to register them
      window.AppEcho.private(`chat_messages.${this.conversation.user.id}`)
        .then(c => {
          if (this.conversation && this.conversation.id !== 'new') {
            c.whisper('seen', {
              conversation_id: this.conversation.id,
            })
          }
        });

      this.$store.dispatch('chat/markConversationAsRead', {conversation: this.conversation, messagesForUserId: this.conversation.user.id});
    }

    if (this.$route.name === 'chat-conversation' && this.isOperator && !this.currentChatUser) {
      this.$router.replace('/chats');
      this.loading = false;
    } else if (!this.$route.params.id || !this.conversation) {
      this.loading = false;
    } else {
      this.getConversation();
    }

    this.scrollBottom();
  },

  unmounted() {
    this.$eventHub.$off('VideoChatCall', this.VideoChatCall);
    this.$eventHub.$off('toggleSidebar', this.toggleSidebar);
    this.$eventHub.$off('toggleSecondSidebar', this.toggleSidebarTwo);
    this.$eventHub.$off('toggleThirdSidebar', this.toggleSidebarThree);
    this.$eventHub.$off('ChatMessage', this.receiveChatMessage);
    this.$eventHub.$off('StartCall', this.StartCall);
    window.removeEventListener('keydown', this.onKeyPress);
  },

  methods: {
    timeFormat,

    StartCall() {
      this.chatScreen = 'call';
    },

    CloseCall() {
      this.chatScreen = 'chat';
    },

    async toggleChatScreen() {
      if (!this.canCallWithAudio && !this.canCallWithVideo) {
        return;
      }

      if (this.chatScreen === 'chat') {
        this.chatScreen = 'call';
      } else {
        this.chatScreen = 'chat';
      }

      if (this.chatScreen === 'call' && !this.onGoingCall) {
        window.Peer.askToConnect(this.conversation.user.id, this.currentChatUser.id);
      }
    },

    lastMessageTimestamp() {
      if (!this.conversation.messages || !this.conversation.messages.length) {
        return null;
      }

      const message = this.conversation.messages[this.conversation.messages.length - 1];

      return message ? message.timestamp : null;
    },

    addEmoji(emoji) {
      this.newMessage += emoji;
      document.querySelector('#chat-input').focus();
    },

    onKeyPress(event) {
      if (event.key === 'Escape') {
        this.closeWindow();
      }
    },

    closeWindow() {
      this.$router.push('/chats');
      this.newMessage = '';
      this.sendTyping(false);
      window.Peer.close(this.currentChatUser.id);
    },

    VideoChatCall() {
      this.CloseCall();
    },

    receiveChatMessage(message) {
      if (message.type === 'call') {
        return;
      }

      this.$nextTick(() => {
        this.scrollBottom();
      });

      if (this.conversation.id && this.$route.params.id === this.conversation.user.username) {
        this.$store.dispatch('chat/markConversationAsRead', {conversation: this.conversation, messagesForUserId: this.conversation.user.id});
      }
    },

    async getConversation() {
      if (this.conversation.id !== 'new') {
        const r = await this.$store.dispatch('chat/getConversation', this.conversation.id);
        await this.$store.commit('chat/updateConversation', {
          conversation_id: this.conversation.id,
          messages: r.data.messages,
        });
        this.$store.dispatch('chat/markConversationAsRead', {conversation: this.conversation, messagesForUserId: this.conversation.user.id});
      }

      this.loading = false;
      this.scrollBottom();
    },

    sendTyping(state) {
      if (!this.conversation || this.conversation.id === 'new') {
        return;
      }

      window.AppEcho.private(`chat_messages.${this.conversation.user.id}`)
        .then(c => {
          if (this.conversation) {
            c.whisper('typing', {
              conversation_id: this.conversation.id,
              typing: state,
            });
          }
        });
    },

    typing() {
      if (this.newMessage && !this.isCurrentUserTyping) {
        this.sendTyping(true);

        this.isCurrentUserTyping = true;
      } else if (!this.newMessage && this.isCurrentUserTyping) {
        this.sendTyping(false);

        this.isCurrentUserTyping = false;
      }
    },

    scrollHandle(evt) {
      //
    },

    toggleSidebar() {
      this.showSidebar = !this.showSidebar;
    },

    toggleSidebarTwo() {
      this.showSidebarTwo = !this.showSidebarTwo;
    },

    toggleSidebarThree() {
      this.showSidebarThree = !this.showSidebarThree;
    },

    closeConversation() {
      this.$eventHub.$emit('toggleChat', false);
      this.$eventHub.$emit('toggleMenu', true);
      this.$router.push('/chats');
      this.message = '';
      this.sendTyping(false);
    },

    getChat(chatId) {
      if (!chatId) {
        return {};
      }

      this.chat = this.chatSidebar.find(el => el.id === parseInt(chatId)) || {};
    },

    messageType(message) {
      if (/[ `!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/.test(message)) {
        return 'text';
      }

      if (/^[A-Za-z0-9]*$/.test(message)) {
        return 'text';
      }

      if (/[a-zA-Z]/g.test(message)) {
        return 'text';
      }

      if (/[0-9]/g.test(message)) {
        return 'text';
      }

      return 'emoji';
    },

    /**
     * @todo: Handle error
     *
     * @returns {Promise<void>}
     */
    async onSendMessage() {
      const message = this.newMessage.trim();

      if (!message) {
        this.newMessage = '';
        return;
      }

      const type = this.messageType(this.newMessage);

      this.$store.dispatch('chat/sendMessage', {
        user_id: this.currentChatUser.id,
        to_user_id: this.conversation.user.id,
        conversationId: this.conversation.id,
        type,
        content: message,
      }).then(r => {
        //
      });

      this.$store.dispatch('chat/addMessageToConversation', {type, content: message, conversation_id: this.conversation.id, user_id: this.currentChatUser.id});

      this.newMessage = '';
      this.sendTyping(false);
      this.isCurrentUserTyping = false;

      this.$nextTick(() => {
        this.scrollBottom();
      });
    },

    scrollBottom() {
      this.$nextTick(() => {
        const chatScroll = document.querySelector('#chat-scroll');

        if (chatScroll) {
          chatScroll.scrollTop = chatScroll.scrollHeight;
        }
      });
    },
  },
};
</script>
